import React from "react";
import { Box } from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import OwnerWorkspaceNavigation from "./components/OwnerWorkspaceNavigation";
import OwnerVoiceOperatorPanel from "./voice/OwnerVoiceOperatorPanel";
import OwnerJarvis2 from "./center/OwnerJarvis2";
import OwnerProjectPendingsDialog from "./projects/OwnerProjectPendingsDialog";
import { OwnerVoiceOperatorProvider } from "./voice/OwnerVoiceOperatorContext";
import { useOwnerJarvisRuntime } from "./jarvis/useOwnerJarvisRuntime";

export default function OwnerJarvisWorkspace() {
  const navigate = useNavigate();
  const location = useLocation();
  const jarvis = useOwnerJarvisRuntime({ navigate });
  const isOwnerHome = /^\/dashboard\/owner\/?$/.test(location.pathname);
  const [pendingProject, setPendingProject] = React.useState(null);

  React.useEffect(() => {
    const openProjectPendings = (event) => {
      const initiativeId = String(event?.detail?.initiativeId || "").trim();
      if (!initiativeId) return;
      setPendingProject({
        initiativeId,
        projectName: String(event?.detail?.projectName || "").trim(),
        token: String(event?.detail?.token || Date.now()),
      });
    };
    window.addEventListener("owner:open-project-pendings", openProjectPendings);
    return () => window.removeEventListener("owner:open-project-pendings", openProjectPendings);
  }, []);

  return (
    <OwnerVoiceOperatorProvider
      actions={jarvis.actions}
      introMessage={jarvis.introMessage}
      onUnhandledUtterance={jarvis.onUnhandledUtterance}
      hasPriorityConversation={jarvis.hasPriorityConversation}
      resetConversationState={jarvis.resetConversationState}
      alwaysOn
      routeContext={location.pathname}
      loadNavigationLearning={jarvis.loadNavigationLearning}
      recordNavigationLearning={jarvis.recordNavigationLearning}
    >
      <Box
        sx={{
          width: "100%",
          height: { xs: "auto", md: "calc(100dvh - 68px)" },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: { xs: "visible", md: "hidden" },
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: { xs: "calc(56px + env(safe-area-inset-top))", sm: "calc(64px + env(safe-area-inset-top))", md: 0 },
            zIndex: 1080,
            flex: "0 0 auto",
            minWidth: 0,
            px: { xs: .45, sm: .65 },
            py: .2,
            bgcolor: "rgba(var(--wp-panel-surface-rgb, 255,255,255),.97)",
            borderBottom: "1px solid",
            borderColor: "divider",
            backdropFilter: "blur(12px)",
          }}
        >
          <OwnerVoiceOperatorPanel persistent pathname={location.pathname} />
        </Box>

        <Box sx={{ display: "flex", flex: "1 1 auto", minHeight: 0, minWidth: 0 }}>
          <OwnerWorkspaceNavigation />
          <Box
            component="section"
            data-owner-mobile-surface="true"
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              minHeight: 0,
              width: "100%",
              overflow: { xs: "visible", md: "auto" },
              overscrollBehavior: "contain",
              px: { xs: .75, sm: .9, md: 1.15 },
              pt: { xs: .65, md: .9 },
              pb: { xs: "calc(72px + env(safe-area-inset-bottom))", md: .9 },
              "& > *": { maxWidth: "none !important", mx: "0 !important" },
              "& .MuiCard-root, & .MuiPaper-root": { minWidth: 0, maxWidth: "100%" },
              "& .MuiTableContainer-root": { maxWidth: "100%", WebkitOverflowScrolling: "touch" },
              "& .MuiFormControl-root, & .MuiTextField-root": { minWidth: 0, maxWidth: "100%" },
              "& .MuiButton-root": { touchAction: "manipulation" },
            }}
          >
            {!isOwnerHome ? <Box sx={{ mb: .75, display: { xs: "none", sm: "block" } }}><OwnerJarvis2 compact /></Box> : null}
            <Outlet />
          </Box>
        </Box>
      </Box>

      <OwnerProjectPendingsDialog
        open={Boolean(pendingProject?.initiativeId)}
        initiativeId={pendingProject?.initiativeId || ""}
        projectName={pendingProject?.projectName || ""}
        onClose={() => setPendingProject(null)}
        onOpenProject={(initiativeId) => {
          setPendingProject(null);
          navigate(`/dashboard/owner/proyectos/${encodeURIComponent(String(initiativeId))}`, {
            state: { ownerJarvisUi: { surface: "project360", initiativeId, section: "project-work", token: `project-pendings:${initiativeId}:${Date.now()}` } },
          });
        }}
      />
    </OwnerVoiceOperatorProvider>
  );
}
